import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  getAvailableClinicians,
  listAgencyClinicPhysicians,
  listEpisodesForPatient,
} from '../../services/api';
import type {
  AvailableClinician,
  ClinicPhysicianResponse,
} from '../../services/api';
import type { EpisodeSummaryResponse } from '../../services/api/types.gen';
import { EventsTab } from './EventsTab';

interface PatientEventsProps {
  patientId: string;
  /** Clinic the visit belongs to; scopes the clinician and physician rosters. */
  agencyClinicId?: string | null;
  /** Episode the visit belongs to; a new log is filed against it. */
  episodeId?: string | null;
  /** Episode window; bounds the date pickers on a new log. */
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
}

/**
 * The patient's fall and infection logs, ready to embed in a visit note.
 *
 * `EventsTab` was written for the patient page, which already holds the episode,
 * clinician and physician rosters its dialogs need. A visit note holds none of
 * them, and threading three arrays down through `VisitDetailPanel` and
 * `OASISForm` would defeat the memo that keeps the question tree from
 * re-rendering on every keystroke. So this wrapper loads them itself, the same
 * way `PatientWounds` loads its own wounds.
 *
 * The three rosters are read once on mount and not refreshed, matching how the
 * patient page caches them: a clinician added to the clinic while the note is
 * open shows up the next time the note is opened.
 */
export const PatientEvents: React.FC<PatientEventsProps> = ({
  patientId,
  agencyClinicId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
}) => {
  const [episodes, setEpisodes] = useState<EpisodeSummaryResponse[]>([]);
  const [clinicians, setClinicians] = useState<AvailableClinician[]>([]);
  const [physicians, setPhysicians] = useState<ClinicPhysicianResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    // Each roster falls back to empty on its own rather than failing the set:
    // the logs are the point of the section, and they stay readable even when a
    // picker inside a dialog comes up short.
    const load = async () => {
      const [episodeResult, clinicianResult, physicianResult] =
        await Promise.all([
          listEpisodesForPatient({ path: { patient_id: patientId } }).catch(
            err => {
              console.error('Failed to load episodes for events:', err);
              return null;
            }
          ),
          getAvailableClinicians({
            query: agencyClinicId ? { clinic_id: agencyClinicId } : {},
          }).catch(err => {
            console.error('Failed to load clinicians for events:', err);
            return null;
          }),
          // Deliberately not `useClinicPhysicians`: that hook drops physicians
          // without an NPI, and the fall and infection dialogs record who was
          // notified, which does not need one.
          agencyClinicId
            ? listAgencyClinicPhysicians({
                path: { agency_clinic_id: agencyClinicId },
              }).catch(err => {
                console.error('Failed to load physicians for events:', err);
                return null;
              })
            : Promise.resolve(null),
        ]);

      if (cancelled) return;
      setEpisodes(episodeResult?.data?.episodes ?? []);
      // Only an assignable clinician can own an event follow-up, which is the
      // same list the patient page's Events tab is given.
      setClinicians(
        (clinicianResult?.data ?? []).filter(c => c.is_assignable_clinician)
      );
      setPhysicians(physicianResult?.data ?? []);
      setIsLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [patientId, agencyClinicId]);

  // Held back rather than rendered with empty rosters: the edit dialogs read
  // their date bounds off `episodes`, so opening one mid-load would relax them.
  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
      </div>
    );
  }

  return (
    <EventsTab
      patientId={patientId}
      episodeId={episodeId}
      episodeStartDate={episodeStartDate}
      episodeEndDate={episodeEndDate}
      episodes={episodes}
      clinicians={clinicians}
      physicians={physicians}
    />
  );
};
